/**
 * Pure helpers that turn a widget's `background` setting into CSS.
 * Shared by every render path (editor canvas, viewer, template preview),
 * so the overlay behaves identically everywhere.
 */

/**
 * @param {string} color - Any CSS color; hex (`#abc` / `#aabbcc`) is converted
 *   to rgba, other formats fall back to `color-mix`
 * @param {number} opacityPercent - Opacity in percent (0 = invisible, 100 = solid)
 * @returns {string} A CSS color with the requested opacity applied
 */
export const colorWithOpacity = (color, opacityPercent) => {
    const pct = Math.min(100, Math.max(0, opacityPercent));

    const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color);
    if (!hexMatch) {
        return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
    }

    let hex = hexMatch[1];
    if (hex.length === 3) {
        hex = hex.split('').map((c) => c + c).join('');
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const alpha = pct / 100;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * @param {import('../controls/grouped/BackgroundControl').BackgroundControlValue} [background]
 * @returns {Partial<CSSStyleDeclaration>} CSS background properties for the widget
 */
export const buildBackgroundStyles = (background) => {
    const image = background?.backgroundImage
        ? `url('${background.backgroundImage}')`
        : 'none';

    /* The overlay is an extra background layer: a flat gradient of the overlay
       color painted on top of the image. Only meaningful when an image is set
       and the color is actually visible. */
    const overlayColor = background?.backgroundOverlayColor;
    const overlayOpacity = background?.backgroundOverlayOpacity ?? 50;
    const hasOverlay = Boolean(background?.backgroundImage) && Boolean(overlayColor) && overlayOpacity > 0;

    const overlay = hasOverlay
        ? colorWithOpacity(overlayColor, overlayOpacity)
        : null;

    const color = background?.backgroundColor;
    const colorOpacity = background?.backgroundColorOpacity ?? 100;

    return {
        backgroundColor: color
            ? (colorOpacity < 100 ? colorWithOpacity(color, colorOpacity) : color)
            : 'transparent',
        backgroundImage: overlay
            ? `linear-gradient(${overlay}, ${overlay}), ${image}`
            : image,
        backgroundRepeat: background?.backgroundRepeat || 'repeat',
        backgroundSize: background?.backgroundSize || 'auto',
        backgroundPosition: background?.backgroundPosition || 'center',
    };
};
